import axios from 'axios';
import type { AxiosError, AxiosRequestConfig } from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.defellix.com';

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

/**
 * Reads user_id out of a JWT's payload without verifying its signature —
 * this is a client-side UX hint only (the backend is the real auth
 * authority), used to recognize a pending-OAuth token (user_id: 0, minted
 * for a brand-new Google/LinkedIn signup with no refresh token yet) so the
 * response interceptor can tell "this token can't refresh, by design" apart
 * from "this session is actually dead."
 */
function decodeJwtUserId(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { user_id?: number };
    return typeof claims.user_id === 'number' ? claims.user_id : null;
  } catch {
    return null;
  }
}

/**
 * True when the currently-stored access token is a pending-OAuth token
 * (user_id: 0) — a brand-new Google/LinkedIn signup that hasn't completed
 * profile setup yet. Its /users/me 401 is expected and must NOT tear down
 * the session. Exported so every teardown path (the interceptor here AND
 * AuthContext's own refetch catch) shares one definition instead of each
 * independently deciding to wipe the token.
 */
export function isPendingOAuthToken(token?: string | null): boolean {
  const t = token ?? getAccessToken();
  return !!t && decodeJwtUserId(t) === 0;
}

/** Persist tokens from a login/refresh response. Refresh token is optional (OAuth keeps it in an httpOnly cookie). */
export function setTokens(access?: string | null, refresh?: string | null) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch {
    /* storage unavailable (private mode) — request interceptor falls back to cookie auth */
  }
  if (access) apiClient.defaults.headers.common.Authorization = `Bearer ${access}`;
}

export function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
  delete apiClient.defaults.headers.common.Authorization;
}

/**
 * API client. Sends the stored access token as a Bearer header (API services require it)
 * AND credentials so the httpOnly refresh cookie reaches POST /auth/refresh.
 */
export const apiClient = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach the current access token to every request (survives reloads — single source of truth).
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Silent refresh on 401 ───────────────────────────────────────────────────
// A single in-flight refresh is shared by all concurrent 401s (no stampede).
let refreshPromise: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  try {
    const body: Record<string, string> = {};
    let rt: string | null = null;
    try {
      rt = localStorage.getItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
    if (rt) body.refresh_token = rt;
    // Bare axios (not apiClient) to avoid interceptor recursion.
    const res = await axios.post(`${API_BASE}/api/v1/auth/refresh`, body, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
    const data = res.data?.data ?? res.data;
    const access: string | null = data?.access_token ?? data?.accessToken ?? null;
    const refresh: string | null = data?.refresh_token ?? data?.refreshToken ?? null;
    if (access) setTokens(access, refresh);
    return access;
  } catch {
    return null;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';
    const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/login');
    // A pending OAuth signup (brand-new Google/LinkedIn user, no real user
    // record yet) intentionally has no refresh token — its access token is
    // still valid for finishing profile setup even though `/users/me` 401s.
    // Callers mark that expected probe so a failed refresh here doesn't wipe
    // the still-good token out from under the rest of the signup flow.
    const isSoftProbe = Boolean((original?.headers as Record<string, string> | undefined)?.['X-Soft-Auth-Probe']);

    if (status === 401 && original && !original._retry && !isAuthCall && !isSoftProbe) {
      // Snapshot the token this request actually failed with — a page that
      // just landed from an OAuth redirect (AuthProvider mounts fresh on
      // every full page load, e.g. main.tsx) fires its own unguarded
      // /users/me refetch() at the same moment SignUp.tsx is exchanging the
      // OAuth code. If that refetch's 401 loses the race and resolves AFTER
      // the exchange has stored a fresh pending token, blindly clearing
      // "whatever's in localStorage now" wipes a token that isn't dead —
      // it just hadn't been issued yet when this request was sent.
      const tokenAtFailureTime = getAccessToken();

      // A pending-OAuth token (user_id: 0) has no refresh token *by design*
      // — refresh failing on it is expected, not a sign of a dead session.
      // AuthProvider's own app-wide /users/me refetch (contexts/AuthContext.tsx)
      // isn't a caller-marked soft probe like Login/SignUp's, and it fires on
      // every fresh page mount — including the OAuth redirect landing — so
      // it deterministically hits this 401 for every brand-new signup.
      // Without this check it would clear the still-good pending token every
      // single time, race or no race.
      if (isPendingOAuthToken(tokenAtFailureTime)) {
        return Promise.reject(error);
      }

      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newAccess = await refreshPromise;
      if (newAccess) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`;
        return apiClient(original);
      }
      if (getAccessToken() !== tokenAtFailureTime) {
        // Superseded by a newer token while the refresh attempt was in
        // flight — that token is still good, so don't touch it.
        return Promise.reject(error);
      }
      // Refresh failed → session is truly over. Clear the dead tokens, then
      // force a login redirect ONLY from the authed app shell (a user working
      // in the dashboard whose session expired mid-use). Everywhere else —
      // landing, pricing, legal pages, public review/sign/verify links — the
      // page must simply render logged-out. The old allowlist-of-public-pages
      // approach regressed every time a new public page shipped (landing got
      // bounced to /login for any visitor with a stale token); protected
      // routes have their own <ProtectedRoute> guard at mount anyway.
      clearTokens();
      try {
        if (typeof window !== 'undefined') {
          if (window.BroadcastChannel) new BroadcastChannel('auth').postMessage('logout');
          const path = window.location.pathname;
          const isProtectedPage =
            path.startsWith('/dashboard') ||
            path.startsWith('/submit-milestone');
          if (isProtectedPage) {
            window.location.assign('/login?session=expired');
          }
        }
      } catch {
        /* ignore */
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Set Bearer token for this session (e.g. OAuth callback). Persists so reloads
 * and refresh work. Pass `refreshToken` whenever the login/verify/exchange
 * response included one — most callers were dropping it on the floor, which
 * meant the ONLY way to silently refresh was the httpOnly cookie. That cookie
 * is SameSite=Strict, so it's never sent cross-site (e.g. a localhost:5173
 * frontend calling api.defellix.com) — with no localStorage fallback, the
 * access token's expiry became a hard, unexpected logout instead of a silent
 * refresh.
 */
export function setSessionToken(token: string | null, refreshToken?: string | null) {
  if (token) setTokens(token, refreshToken ?? undefined);
  else clearTokens();
}

export { API_BASE };
